/**
 * Projektets byggstart och slutdatum.
 *
 * Tidplanen är den källa som gäller. Project.startDate är ett Mixed-fält som
 * fylls vid konvertering från upphandling med det som stod i offerten, ofta
 * fritext som "18 augusti", "Q1 2026" eller "vecka 35". Så snart en tidplan
 * finns är det dess projektrad som är sanningen — det är den byggare, kund och
 * konsulter arbetar efter.
 *
 * Mönstret fanns redan i BuilderProjectCard.js:82 men saknades i projektvyernas
 * rubriker, som därför visade fritexten. För Oscar Eriksson (#90577) stod det
 * "18 augusti" i rubriken medan tidplanen sade den 19:e.
 */

/**
 * Är värdet otvetydigt ett datum?
 *
 * V8:s Date-parser tolkar fritext: "18 augusti" blir 2001-08-17 och
 * "vecka 35" blir 2034-12-31. Ett påhittat datum är värre än inget alls,
 * så bara Date-objekt och ISO-strängar godtas.
 */
export const arDatum = (varde) => {
  if (!varde) return false;
  if (varde instanceof Date) return !isNaN(varde.getTime());
  if (typeof varde !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}/.test(varde) && !isNaN(new Date(varde).getTime());
};

/**
 * Rådatum för byggstart, i prioritetsordning tidplan → projekt → ursprungligt.
 * @param {Object} project
 * @returns {string|Date|null}
 */
export const hamtaByggstart = (project) =>
  project?.timeline?.startDate || project?.startDate || project?.originalStartDate || null;

/**
 * Rådatum för slutdatum, samma prioritetsordning.
 * @param {Object} project
 * @returns {string|Date|null}
 */
export const hamtaSlutdatum = (project) =>
  project?.timeline?.endDate || project?.endDate || project?.originalEndDate || null;

/**
 * Formaterar ett projektdatum för visning.
 *
 * Går värdet inte att tolka som datum skrivs det ut som det är — en fritext
 * som "vecka 35" är mer användbar för läsaren än ett tomt fält.
 *
 * @param {string|Date|null} varde
 * @param {Object} [opts] - Intl-optioner, default "2 mars 2026"
 * @returns {string}
 */
export const formateraProjektdatum = (varde, opts) => {
  if (!varde) return '';

  // Fritext skrivs ut som den är — "vecka 35" är mer användbart för läsaren
  // än ett påhittat datum eller ett tomt fält
  if (!arDatum(varde)) return String(varde);

  const d = new Date(varde);
  if (isNaN(d.getTime())) return String(varde);

  return d.toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    // Datum lagras som lokal midnatt i UTC-skal. Utan tidszon visar en
    // UTC-server dagen innan.
    timeZone: 'Europe/Stockholm',
    ...opts
  });
};

/** Byggstart färdigformaterad för visning. */
export const formateraByggstart = (project, opts) =>
  formateraProjektdatum(hamtaByggstart(project), opts);
